import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useRef, useState } from "react";
import styled from "styled-components";
import { MenuListItem } from "../../menu-list-item/index";
import { SelectMessageOption } from "./SelectMessageOption";
import { SelectLoadingOption } from "./SelectLoadingOption";
import { SelectBackOption } from "./SelectBackOption";
import { SelectOption } from "./SelectOption";
import { SelectOptionCheckbox } from "./SelectOptionCheckbox";
import { getFilteredOptionList } from "./OptionListHandler.util";
import { useNoOptionsMessage } from "./useNoOptionsMessage";
const GroupSeparator = styled(MenuListItem.Separator) `
  margin: 0;
  padding: 0;
`;
const optionHasChildren = (option) => option.hasChildren || option.options !== undefined;
const OptionListHandlerComponent = ({ back, currentOptions, depth, hasCheckboxes, hasReachedSelectedLimit, isLoading, isSearchable, loadingProps, noOptionsMessage, noSearchResultsMessage, onUpdate, onApply, // TODO replace with hasFooter
options, parentOption, push, renderNestedGroupLabel, searchText, selectedOption, selectedOptions, selectedValue, closeAndReturnFocus, stringMap, 
// FIXME: this is actually required despite being deprecated and optional
overlayRef }) => {
    const hasFooter = Boolean(onApply);
    const [isFetchingAsync, setIsFetchingAsync] = useState(false);
    const backRef = useRef(null);
    const noOptionsRef = useRef(null);
    const derivedOptions = getFilteredOptionList({
        currentOptions,
        options,
        searchText
    });
    // FIXME: rewrite this without using deprecated APIs
    const focusOnBackOption = () => {
        requestAnimationFrame(() => {
            // Should still work when OptionListHandler is used without SelectMenu
            if (backRef && backRef.current) {
                backRef.current.focus();
                return;
            }
            // Should work with updated SelectMenu (backRef is going to become empty when switching between contexts)
            if (overlayRef?.current) {
                const backOption = overlayRef.current.querySelector(`[data-back-option]`);
                backOption && backOption.focus();
            }
        });
    };
    const getNavigateFunction = (option, parentId) => {
        if (typeof option.options === "function") {
            const fetchOptions = option.options;
            const asyncCall = async () => {
                setIsFetchingAsync(true);
                const result = await fetchOptions(option);
                setIsFetchingAsync(false);
                option.parentId = parentId;
                push(option, result, derivedOptions);
                focusOnBackOption();
            };
            return asyncCall;
        }
        return () => {
            option.parentId = parentId;
            push(option, Array.isArray(option.options) ? option.options : undefined, derivedOptions);
            focusOnBackOption();
        };
    };
    const getEmptyResultsMessage = () => {
        const hasSearchText = options && searchText && searchText.length > 0;
        if (hasSearchText) {
            return noSearchResultsMessage || noOptionsMessage;
        }
        return noOptionsMessage;
    };
    const optionIsSelected = (option) => {
        if (selectedValue) {
            return option.value === selectedValue;
        }
        if (selectedOption) {
            return option.value === selectedOption.value;
        }
        if (selectedOptions) {
            const { value } = option;
            return selectedOptions.some((obj) => value === obj.value);
        }
        return false;
    };
    const getOptionUpdateFunction = (option, isSelected, parentId) => {
        if (!hasCheckboxes && optionHasChildren(option)) {
            return getNavigateFunction(option, parentId);
        }
        if (hasReachedSelectedLimit) {
            return isSelected ? onUpdate : undefined;
        }
        return option.disabled ? undefined : onUpdate;
    };
    const isParentOptionSelected = parentOption && optionIsSelected(parentOption);
    const SelectOptionComponent = hasCheckboxes ? SelectOptionCheckbox : SelectOption;
    const { hasNoOptionsMessage, screenReaderContent } = useNoOptionsMessage(noOptionsRef, derivedOptions, getEmptyResultsMessage(), isLoading);
    /**
     * TODO: this is broken when navigating into a nested async layer
     * back button doesn't spawn when navigating from top level into first layer of nesting
     * it is rendered when trying to go deeper (but doesn't actually cancel navigation) and likely keeps referencing the skip level
     * */
    const hasBackOption = parentOption && depth;
    const hasParentOption = parentOption && hasCheckboxes;
    return (_jsxs(_Fragment, { children: [hasBackOption && (_jsx(SelectBackOption, { ref: backRef, 
                // When using checkboxes, the parent label renders a parent checkbox, so don't duplicate it here.
                label: hasCheckboxes ? undefined : parentOption.label, parentId: parentOption && parentOption.parentId ? parentOption.parentId : "", onClick: back, hasCheckboxes: hasCheckboxes, isSearchable: isSearchable, overlayRef: overlayRef, searchText: searchText, closeAndReturnFocus: closeAndReturnFocus, stringMap: stringMap, "data-back-option": true })), isFetchingAsync ? (_jsx(SelectLoadingOption, {}) // TODO: add aria-busy to SelectMenu instead
            ) : (_jsxs(_Fragment, { children: [hasParentOption && (_jsx(SelectOptionComponent, { onClick: (_, onSelect) => {
                            // TODO: Clean up confusion here: onClick is called with custom options sometimes, and sometimes called with an Event
                            const onUpdate = getOptionUpdateFunction(parentOption, optionIsSelected(parentOption));
                            if (onUpdate) {
                                onUpdate(parentOption, onSelect);
                            }
                        }, isNested: false, isParent: true, hasChildren: false, isSelected: isParentOptionSelected, isHighlighted: false, ...parentOption, closeAndReturnFocus: closeAndReturnFocus, checkStyle: !isParentOptionSelected && parentOption.checkStyle ? parentOption.checkStyle : undefined, hasFooter: hasFooter, isSearchable: isSearchable, searchText: searchText, stringMap: stringMap, renderNestedGroupLabel: renderNestedGroupLabel }, parentOption.value)), derivedOptions &&
                        derivedOptions.map((option, index) => {
                            const hasChildren = optionHasChildren(option);
                            const isSelected = optionIsSelected(option);
                            const isFirst = index === 0 && !hasBackOption && !isSearchable && !hasCheckboxes;
                            const isLast = index === derivedOptions.length - 1 && !hasFooter && !isLoading;
                            const isFirstStandalone = isFirst && !option.groupItems;
                            const isLastStandalone = isLast && !option.groupItems;
                            const key = option.id ? option.id : `i-${index}`;
                            if (option.isSeparator) {
                                return _jsx(MenuListItem.Separator, { role: "separator" }, key);
                            }
                            if (option.groupItems && searchText) {
                                // when searching, parent group items appear as descriptions within their child items
                                return null;
                            }
                            if (option.groupItems && !searchText) {
                                return (_jsxs(React.Fragment, { children: [index === 0 ? null : _jsx(GroupSeparator, { role: "separator" }), _jsxs(MenuListItem.Group, { ...option, role: "group", "aria-labelledby": `${key}-group-title`, children: [option.label && (_jsx(MenuListItem.GroupTitle, { "aria-hidden": true, id: `${key}-group-title`, children: typeof option.label === "function" ? option.label({ searchText }) : option.label })), option.groupItems.map((groupItem, groupIndex, self) => {
                                                    const groupItemHasChildren = optionHasChildren(groupItem);
                                                    const isGroupItemSelected = optionIsSelected(groupItem);
                                                    const isFirstGroupItem = isFirst && groupIndex === 0 && !option.label;
                                                    const isLastGroupItem = isLast && groupIndex === self.length - 1;
                                                    return (_jsx(SelectOptionComponent, { parentId: `${depth}-${index}`, onClick: (_, onSelect) => {
                                                            const onUpdate = getOptionUpdateFunction(groupItem, isGroupItemSelected, `${depth}-${index}`);
                                                            if (onUpdate) {
                                                                onUpdate(groupItem, onSelect);
                                                            }
                                                        }, isNested: parentOption !== undefined, hasChildren: groupItemHasChildren, 
                                                        // Allow either the whole group or just one of the items be selected
                                                        isSelected: isGroupItemSelected, isHighlighted: groupItem.isHighlighted, ...groupItem, closeAndReturnFocus: closeAndReturnFocus, checkStyle: !isGroupItemSelected && groupItem.checkStyle ? groupItem.checkStyle : undefined, onIconClick: getNavigateFunction(groupItem, `${depth}-${index}`), isSearchable: isSearchable, isFirst: isFirstGroupItem, isLast: isLastGroupItem, searchText: searchText, stringMap: stringMap }, groupItem.value));
                                                })] })] }, key));
                            }
                            return (_jsx(SelectOptionComponent, { parentId: `${depth}-${index}`, onClick: (_, onSelect) => {
                                    // TODO: Clean up confusion here: onClick is called with custom options sometimes, and sometimes called with an Event
                                    const onUpdate = getOptionUpdateFunction(option, isSelected, `${depth}-${index}`);
                                    if (onUpdate) {
                                        onUpdate(option, onSelect);
                                    }
                                }, isNested: parentOption !== undefined, hasChildren: hasChildren, isSelected: isSelected, isHighlighted: option.isHighlighted, isFirst: isFirstStandalone, isLast: isLastStandalone, renderNestedGroupLabel: renderNestedGroupLabel, ...option, closeAndReturnFocus: closeAndReturnFocus, checkStyle: !isSelected && option.checkStyle ? option.checkStyle : undefined, onIconClick: getNavigateFunction(option, `${depth}-${index}`), hasFooter: hasFooter, isSearchable: isSearchable, searchText: searchText, stringMap: stringMap }, option.value || (typeof option.label === "string" ? option.label : key)));
                        })] })), _jsx(SelectMessageOption, { isVisuallyHidden: !hasNoOptionsMessage, ref: noOptionsRef, children: hasNoOptionsMessage && getEmptyResultsMessage() }), _jsx(SelectMessageOption, { isVisuallyHidden: true, children: _jsx("div", { "aria-live": "assertive", children: screenReaderContent }) }), isLoading && !isFetchingAsync && _jsx(SelectLoadingOption, { ...loadingProps })] }));
};
export const OptionListHandler = memo(OptionListHandlerComponent);
